import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import TrustBlock from '../components/common/TrustBlock';
import { Check, Calendar, Truck, Clock, ArrowRight, Star, Phone, Download } from 'lucide-react';
import MobileStickyBar from '../components/common/MobileStickyBar';
import { formatDate } from '../utils/dateUtils';
import { fetchDocLinks } from '../utils/docLinks';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PaiementSuccesPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const quoteId = searchParams.get('quote_id');
  const [status, setStatus] = useState('checking');
  const [quote, setQuote] = useState(null);
  const [factures, setFactures] = useState([]);
  const [docLinks, setDocLinks] = useState(null);

  useEffect(() => {
    if (sessionId) {
      pollStatus();
    } else {
      setStatus('paid');
    }
    if (quoteId) fetchQuote();
  // eslint-disable-next-line
  }, [sessionId, quoteId]);

  // Facture d'acompte (ANO-ECC-06) — l'émission est asynchrone après paiement → on ré-essaie
  useEffect(() => {
    if (status === 'paid' && quoteId) fetchFactures(0);
  // eslint-disable-next-line
  }, [status, quoteId]);

  async function fetchFactures(attempts) {
    try {
      const res = await fetch(`${BACKEND}/api/devis/${quoteId}/factures`);
      if (res.ok) {
        const d = await res.json();
        if ((d.factures || []).length > 0) {
          setFactures(d.factures);
          // Liens signés (ANO-ECC-04) autorisés par la session de paiement Stripe
          const tok = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth') || undefined;
          const links = await fetchDocLinks(BACKEND, quoteId, { sessionId, token: tok });
          if (links) setDocLinks(links);
          return;
        }
      }
    } catch (e) { /* réessai */ }
    if (attempts < 5) setTimeout(() => fetchFactures(attempts + 1), 2000);
  }

  async function pollStatus(attempts = 0) {
    if (attempts >= 6) { setStatus('timeout'); return; }
    try {
      const res = await fetch(`${BACKEND}/api/paiement/status/${sessionId}`);
      const data = await res.json();
      if (data.payment_status === 'paid') {
        setStatus('paid');
        return;
      }
      setTimeout(() => pollStatus(attempts + 1), 2000);
    } catch (e) {
      setTimeout(() => pollStatus(attempts + 1), 2000);
    }
  }

  async function fetchQuote() {
    try {
      const res = await fetch(`${BACKEND}/api/devis/${quoteId}`);
      if (res.ok) setQuote(await res.json());
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.error('[PaiementSucces] fetchQuote:', e);
    }
  }

  // LOADING
  if (status === 'checking') {
    return (
      <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
        <Header />
        <div className="container-ose flex items-center justify-center" style={{ minHeight: '70vh' }}>
          <div className="text-center">
            <div className="animate-spin w-16 h-16 rounded-full mx-auto mb-6"
              style={{ border: '3px solid var(--border)', borderTopColor: 'var(--teal)' }} />
            <h1 className="font-display text-xl font-bold mb-2" style={{ color: 'var(--ink)' }}>
              Confirmation en cours…
            </h1>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Patientez quelques secondes</p>
          </div>
        </div>
        <MobileStickyBar />
      <Footer />
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      <div className="container-ose" style={{ paddingTop: '2rem', paddingBottom: '4rem', maxWidth: '640px' }}>
        {/* Success banner */}
        <div
          className="text-center p-8 rounded-3xl mb-6"
          style={{
            background: 'linear-gradient(135deg, var(--teal-mist) 0%, #FFFFFF 100%)',
            border: '2px solid rgba(93,188,191,0.3)',
            boxShadow: '0 20px 60px rgba(93,188,191,0.12)'
          }}
        >
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ background: 'var(--teal)', boxShadow: '0 12px 32px rgba(93,188,191,0.3)' }}>
            <Check size={36} color="#0B1E20" strokeWidth={3} />
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mb-2"
            style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
            {status === 'paid' ? 'Acompte reçu ✓' : 'Demande envoyée ✓'}
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>
            {status === 'paid'
              ? 'Votre réservation est confirmée. Notre équipe vous contacte prochainement.'
              : 'Votre demande a bien été reçue. Nous la traitons dans les plus brefs délais.'}
          </p>
        </div>

        {/* Quote summary */}
        {quote && (
          <div className="rounded-2xl p-5 mb-5" style={{ background: '#FFFFFF', border: '1px solid var(--border)', boxShadow: '0 4px 16px rgba(26,26,46,0.05)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>Récapitulatif de la réservation</h2>
              <span className="text-xs font-mono-ose font-bold" style={{ color: 'var(--teal)' }}>{quote.numero}</span>
            </div>

            {/* Products */}
            {quote.items?.slice(0, 3).map((item, i) => (
              <div key={`product-${i}`} className="flex items-center gap-3 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
                <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0" style={{ border: '1px solid var(--border)' }}>
                  <div className="w-full h-full" style={{ background: 'var(--teal-mist)' }} />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>{item.product_nom}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{item.quantite}× {item.product_type}</p>
                </div>
                <span className="text-xs price-gold font-bold">{item.sous_total?.toFixed(2)}€</span>
              </div>
            ))}

            {/* Key infos */}
            <div className="grid grid-cols-2 gap-3 mt-4">
              {[
                {
                  icon: <Calendar size={14} style={{ color: 'var(--teal)' }} />,
                  label: "Date événement",
                  value: quote.session?.date_evenement
                    ? formatDate(quote.session.date_evenement)
                    : 'À confirmer'
                },
                {
                  icon: <Truck size={14} style={{ color: 'var(--teal)' }} />,
                  label: "Livraison",
                  value: quote.session?.date_livraison || 'À confirmer'
                },
                {
                  icon: <Clock size={14} style={{ color: '#C58B19' }} />,
                  label: "Retour matériel",
                  value: "Lendemain avant 10H00"
                },
                {
                  icon: <Check size={14} style={{ color: '#1F9D7A' }} />,
                  label: "Acompte payé",
                  value: `${quote.acompte?.toFixed(2)}€ (30%)`
                },
              ].map((info, i) => (
                <div key={`list-${i}`} className="flex items-start gap-2 p-3 rounded-xl"
                  style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <div className="mt-0.5">{info.icon}</div>
                  <div>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{info.label}</p>
                    <p className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>{info.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="flex items-center justify-between pt-3 mt-2" style={{ borderTop: '1px solid var(--border)' }}>
              <div>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Total TTC</p>
                <p className="text-xl font-bold price-gold">{quote.total?.toFixed(2)}€</p>
              </div>
              <div className="text-right">
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Solde restant (à la livraison)</p>
                <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{quote.solde?.toFixed(2)}€</p>
              </div>
            </div>
          </div>
        )}

        {/* Return reminder — STRONG */}
        <div className="flex items-start gap-3 p-4 rounded-2xl mb-5"
          style={{ background: 'rgba(197,139,25,0.08)', border: '2px solid rgba(197,139,25,0.3)' }}>
          <Clock size={20} style={{ color: '#C58B19', flexShrink: 0 }} />
          <div>
            <p className="font-bold text-sm" style={{ color: '#C58B19' }}>
              ⚠️ Rappel important : retour du matériel
            </p>
            <p className="text-xs mt-1" style={{ color: '#7A5200', lineHeight: '1.6' }}>
              Tout le matériel loué doit être rendu au dépôt Show Événement
              <strong> le lendemain de votre événement avant 10H00 maximum.</strong>
              <br />Au-delà, une demi-journée supplémentaire sera facturée automatiquement.
            </p>
          </div>
        </div>

        {/* What's next */}
        <div className="rounded-2xl p-5 mb-5" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
          <h2 className="font-semibold text-sm mb-4" style={{ color: 'var(--ink)' }}>Prochaines étapes</h2>
          <div className="space-y-3">
            {[
              { step: '1', text: 'Email de confirmation envoyé à votre adresse', done: true },
              { step: '2', text: 'Notre équipe vous rappelle sous 24H pour confirmer les détails', done: false },
              { step: '3', text: 'Livraison et installation à votre lieu d\'événement', done: false },
              { step: '4', text: 'Retour matériel le lendemain avant 10H00', done: false },
            ].map((s, i) => (
              <div key={`step-${i}`} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
                  style={{ background: s.done ? 'var(--teal)' : 'var(--bg)', color: s.done ? '#0B1E20' : 'var(--text-muted)', border: `1px solid ${s.done ? 'var(--teal)' : 'var(--border)'}` }}>
                  {s.done ? <Check size={12} /> : s.step}
                </div>
                <p className="text-xs" style={{ color: s.done ? 'var(--ink)' : 'var(--text-muted)', lineHeight: '1.5' }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          {quoteId && (
            <Link to={`/devis/${quoteId}`} data-testid="succes-voir-devis-btn"
              className="flex-1 flex items-center gap-2 justify-center px-5 py-3 rounded-xl text-sm font-semibold"
              style={{ background: 'var(--teal)', color: '#0B1E20' }}>
              <Download size={15} /> Voir / télécharger mon devis PDF
            </Link>
          )}
          <a href="tel:0756876665"
            className="flex-1 flex items-center gap-2 justify-center px-5 py-3 rounded-xl text-sm font-semibold"
            style={{ background: '#FFFFFF', color: 'var(--ink)', border: '1.5px solid var(--border)' }}>
            <Phone size={15} style={{ color: 'var(--teal)' }} /> 07 56 87 66 65
          </a>
        </div>

        {/* Facture d'acompte (ANO-ECC-06), liens signés (ANO-ECC-04) */}
        {docLinks && (docLinks.factures || []).length > 0 && (
          <div className="rounded-2xl p-5 mb-6" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }} data-testid="succes-factures">
            <h2 className="font-semibold text-sm mb-3" style={{ color: 'var(--ink)' }}>Votre facture</h2>
            <div className="flex flex-col gap-2">
              {docLinks.factures.map((fac) => (
                <a key={fac.id} href={`${BACKEND}${fac.url}`} target="_blank" rel="noreferrer"
                  data-testid={`succes-facture-${fac.type}`}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium"
                  style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)', textDecoration: 'none' }}>
                  <Download size={15} style={{ color: '#C58B19' }} /> {fac.type === 'acompte' ? "Facture d'acompte" : 'Facture finale'} {fac.numero} (PDF)
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Trust */}
        <TrustBlock compact />

        {/* Google review */}
        <div className="text-center mt-6 p-4 rounded-2xl" style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.2)' }}>
          <div className="flex justify-center gap-0.5 mb-2">
            {[1,2,3,4,5].map(s => <Star key={s} size={14} fill="var(--gold)" color="var(--gold)" />)}
          </div>
          <p className="text-sm font-semibold mb-1" style={{ color: '#0B4B4E' }}>
            Votre avis compte pour nous !
          </p>
          <p className="text-xs mb-3" style={{ color: 'rgba(11,75,78,0.7)' }}>
            Après votre événement, laissez-nous un avis sur Google.
          </p>
          <a href="https://share.google/hZSHglw36RKeQgqnp" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold"
            style={{ background: 'var(--teal)', color: '#0B1E20' }}>
            <Star size={12} fill="#0B1E20" color="#0B1E20" />
            Laisser un avis Google après ma prestation
          </a>
        </div>
      </div>
      <MobileStickyBar />
      <Footer />
    </div>
  );
}
